import React from 'react';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import ApiService from '../../../service/apiService';
import styles from './LocationCard.module.css';
import cardStyles from '../Card.module.css';
import { capitalizeFirstLetter, parseJSONStringToObject } from '../../../HelperMethods/UtilityFunctions';
import ContactCard from '../contactCard/ContactCard';
import { getAllAssetAttributesSelector } from '../../generalConfig/attributeManagement/redux/reducer';

const IS_LOADING_STATE = 'Loading...';
const LOADING_FAILED = 'Loading Failed!';

async function getContacts(contactIds) {
  if (_.isEmpty(contactIds)) {
    return null;
  }
  const contactsResponse = await ApiService.getContactsByContactIds(contactIds);
  return contactsResponse;
}

async function getWatchers(watcherIds) {
  if (_.isEmpty(watcherIds)) {
    return null;
  }
  const watchersResponse = await ApiService.getUsersByIDs(watcherIds);
  return _.get(watchersResponse, 'data.getUserByIds', null);
}

async function loadAsset(assetId, setAsset, setContacts, setWatchers) {
  try {
    setAsset(IS_LOADING_STATE);
    const response = await ApiService.getAsset(assetId);
    const responseAsset = _.get(response, 'data.getAsset', null);
    responseAsset.customAttributes = parseJSONStringToObject(responseAsset.customAttributes);
    setAsset(responseAsset);
    setWatchers(IS_LOADING_STATE);
    setContacts(IS_LOADING_STATE);
    try {
      const [contacts, watchers] = await Promise.all([
        getContacts(responseAsset.contacts),
        getWatchers(responseAsset.watchers),
      ]);
      setContacts(contacts);
      setWatchers(watchers);
    } catch (e) {
      setWatchers(LOADING_FAILED);
      setContacts(LOADING_FAILED);
    }
  } catch (e) {
    setAsset(LOADING_FAILED);
  }
}

export default function LocationCard({ locationId }) {
  const [asset, setAsset] = React.useState(null);
  const [contacts, setContacts] = React.useState(null);
  const [watchers, setWatchers] = React.useState(null);
  const assetAttributes = useSelector(getAllAssetAttributesSelector);
  const assetAttributesDict = _.keyBy(assetAttributes, 'attributeName') || {};
  React.useEffect(() => {
    loadAsset(locationId, setAsset, setContacts, setWatchers);
  }, [locationId]);

  return (
    <div className={styles.card}>
      <div className={cardStyles.cardHeader}>
        {typeof asset === 'string' ? (
          asset
        ) : (
          <div className={cardStyles.taskHeaderLines} style={{ paddingLeft: '24px' }}>
            <div className={cardStyles.mainHeader}>{asset?.name}</div>
            <div className={cardStyles.subHeader}>{asset?.type ? capitalizeFirstLetter(asset.type) : ''}</div>
          </div>
        )}
      </div>
      <div className={cardStyles.cardContent}>
        <ContactsComponent contacts={contacts} />
        <CustomAttributesComponent
          customAttributes={asset?.customAttributes}
          assetAttributesDict={assetAttributesDict}
        />
        <WatchersComponent watchers={watchers} />
        <LatLngComponent latitude={asset?.latitude} longitude={asset?.longitude} />
      </div>
    </div>
  );
}

function LatLngComponent({ latitude, longitude }) {
  return (
    <>
      {!(_.isNil(latitude) && _.isNil(longitude)) && (
        <>
          <div className={styles.rowHeader} style={{ marginTop: '10px' }}>
            <div className={cardStyles.rowTitle}>Lat-Lng</div>
          </div>
          {!_.isNil(latitude) && (
            <div className={styles.row}>
              <div className={styles.rowLabel}>Latitude</div>
              <div className={styles.rowValue}>{latitude}</div>
            </div>
          )}
          {!_.isNil(longitude) && (
            <div className={styles.row}>
              <div className={styles.rowLabel}>Longitude</div>
              <div className={styles.rowValue}>{longitude}</div>
            </div>
          )}
        </>
      )}
    </>
  );
}

function WatchersComponent({ watchers }) {
  return (
    <>
      {!_.isNil(watchers) && (
        <>
          <div className={styles.rowHeader} style={{ marginTop: '10px' }}>
            <div className={cardStyles.rowTitle}>Watchers</div>
          </div>
          {typeof watchers === 'string' ? (
            <span>{watchers}</span>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Name</th>
                </tr>
              </thead>
              <tbody>
                {watchers.map((watcher) => (
                  <tr key={watcher.id}>
                    <td>{`${watcher.firstName} ${watcher.lastName}`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </>
  );
}

function ContactsComponent({ contacts }) {
  return (
    <>
      {!_.isNil(contacts) && (
        <>
          <div className={styles.rowHeader} style={{ marginTop: '10px' }}>
            <div className={cardStyles.rowTitle}>Contacts</div>
          </div>
          {typeof contacts === 'string' ? (
            <span>{contacts}</span>
          ) : (
            <>
              {contacts.map((contact) => (
                <ContactCard {...contact} key={contact.id} />
              ))}
            </>
          )}
        </>
      )}
    </>
  );
}

function CustomAttributesComponent({ customAttributes, assetAttributesDict = {} }) {
  return (
    <>
      {!_.isEmpty(customAttributes) && (
        <>
          <div className={styles.rowHeader}>
            <div className={cardStyles.rowTitle}>Custom Attributes</div>
          </div>
          {Object.entries(customAttributes).map(([key, value]) => (
            <div key={key} className={styles.row}>
              <div className={styles.rowLabel}>{assetAttributesDict?.[key]?.attributeDisplayName ?? key}</div>
              <div className={styles.rowValue}>{value}</div>
            </div>
          ))}
        </>
      )}
    </>
  );
}
